import React, { useId, useRef, useState } from 'react';

/**
 * One tab's definition: a stable `key`, its label, and the content to render
 * while active. `content` is only mounted while its tab is active (simpler
 * and lighter than keeping every tab's widgets — several of them recharts —
 * mounted off-screen at once).
 */
export type AnalyticsTabDef = {
  key: string;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
};

/**
 * Accessible, keyboard-navigable tabs shared by every Feature 5.3 persona
 * dashboard, used to split each page into Overview / Analysis / Raw data
 * sections instead of one long scroll. Implements the WAI-ARIA tabs pattern
 * (roving tabindex, Left/Right/Home/End) — `role="tablist"`/`"tab"`/
 * `"tabpanel"` wired up manually since the design system doesn't ship a tabs
 * primitive yet. Usable controlled (`activeKey`/`onChange`) or uncontrolled
 * (`defaultTabKey`, internal state).
 */
export const AnalyticsTabs: React.FC<{
  tabs: AnalyticsTabDef[];
  defaultTabKey?: string;
  activeKey?: string;
  onChange?: (key: string) => void;
}> = ({ tabs, defaultTabKey, activeKey, onChange }) => {
  const uid = useId();
  const [internalActive, setInternalActive] = useState<string | undefined>(defaultTabKey ?? tabs[0]?.key);
  const active = activeKey ?? internalActive ?? tabs[0]?.key;
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const select = (key: string) => {
    if (onChange) onChange(key);
    if (activeKey === undefined) setInternalActive(key);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const idx = tabs.findIndex((t) => t.key === active);
    if (idx === -1) return;
    let nextIdx: number | null = null;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = tabs.length - 1;
    if (nextIdx != null) {
      e.preventDefault();
      const nextKey = tabs[nextIdx].key;
      select(nextKey);
      tabRefs.current[nextKey]?.focus();
    }
  };

  const activeTab = tabs.find((t) => t.key === active) ?? tabs[0];

  if (tabs.length === 0) return null;

  return (
    <div>
      <div
        role="tablist"
        aria-label="Dashboard sections"
        onKeyDown={handleKeyDown}
        className="mb-6 flex flex-wrap items-center gap-1 border-b border-slate-200"
      >
        {tabs.map((tab) => {
          const selected = tab.key === active;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                tabRefs.current[tab.key] = el;
              }}
              id={`${uid}-tab-${tab.key}`}
              role="tab"
              type="button"
              aria-selected={selected}
              aria-controls={`${uid}-panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => select(tab.key)}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors ${
                selected
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>
      {activeTab && (
        <div
          key={activeTab.key}
          id={`${uid}-panel-${activeTab.key}`}
          role="tabpanel"
          aria-labelledby={`${uid}-tab-${activeTab.key}`}
          tabIndex={0}
        >
          {activeTab.content}
        </div>
      )}
    </div>
  );
};

export default AnalyticsTabs;
